import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Settings, Save, ArrowLeft, Coins, MessageSquare, AlertTriangle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [settings, setSettings] = useState({
    max_context_tokens: 8000,
    token_warning_threshold: 0.8
  });
  const [analysisPrompt, setAnalysisPrompt] = useState('');
  const [analysisModel, setAnalysisModel] = useState('anthropic/claude-sonnet-4-5-20250929');
  const [openrouterModels, setOpenrouterModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/create');
      return;
    }
    fetchSettings();
    fetchAnalysisSettings();
    fetchOpenRouterModels();
  }, [user, navigate]);

  const fetchOpenRouterModels = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/openrouter-models`);
      setOpenrouterModels(response.data);
    } catch (error) {
      console.error('獲取模型列表失敗:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/settings`);
      setSettings({
        max_context_tokens: response.data.max_context_tokens || 8000,
        token_warning_threshold: response.data.token_warning_threshold || 0.8
      });
    } catch (error) {
      console.error('獲取設定失敗:', error);
      toast.error('獲取設定失敗');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalysisSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/case-library/admin/analysis-settings`);
      setAnalysisPrompt(response.data.analysis_prompt || '');
      setAnalysisModel(response.data.analysis_model || 'anthropic/claude-sonnet-4-5-20250929');
    } catch (error) {
      console.error('獲取分析設定失敗:', error);
    }
  };

  const saveAnalysisSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/case-library/admin/analysis-settings`, {
        analysis_prompt: analysisPrompt,
        analysis_model: analysisModel
      });
      toast.success('分析 Agent 設定已保存！');
    } catch (error) {
      toast.error('保存失敗');
    } finally {
      setSaving(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/settings`, settings);
      toast.success('設定已保存');
    } catch (error) {
      console.error('保存設定失敗:', error);
      toast.error('保存設定失敗');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Header */}
      <header className="bg-white border-b border-neutral-200 px-8 py-6 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold text-neutral-900">系統設定</h1>
            <p className="text-neutral-600 text-sm mt-1">配置系統參數和功能</p>
          </div>
          <Button
            onClick={saveSettings}
            disabled={saving}
            className="rounded-full"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? '保存中...' : '保存設定'}
          </Button>
        </div>
      </header>

      {/* Content - Scrollable */}
      <main className="flex-1 overflow-y-auto p-8">
        <div className="space-y-8">
          {/* Token Settings Section */}
          <section className="bg-white rounded-2xl p-6 shadow-sm border border-neutral-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">對話 Token 設定</h2>
                <p className="text-sm text-neutral-500">控制每個對話的最大 Token 數量</p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Max Context Tokens */}
              <div className="space-y-3">
                <Label className="text-base font-medium">最大上下文 Tokens</Label>
                <p className="text-sm text-neutral-500">
                  設定每個對話允許的最大 Token 數量。超過此限制後，用戶需要新建對話。
                </p>
                <div className="flex items-center gap-4">
                  <Input
                    type="number"
                    value={settings.max_context_tokens}
                    onChange={(e) => setSettings({
                      ...settings,
                      max_context_tokens: parseInt(e.target.value) || 0
                    })}
                    className="w-32"
                    min={1000}
                    max={100000}
                    step={1000}
                  />
                  <span className="text-neutral-500">tokens</span>
                </div>
                <div className="flex gap-2 mt-2">
                  {[4000, 8000, 16000, 32000].map(value => (
                    <Button
                      key={value}
                      variant={settings.max_context_tokens === value ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSettings({ ...settings, max_context_tokens: value })}
                      className="rounded-full"
                    >
                      {value.toLocaleString()}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Warning Threshold */}
              <div className="space-y-3 pt-4 border-t border-neutral-100">
                <div className="flex items-center gap-2">
                  <Label className="text-base font-medium">警告閾值</Label>
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                </div>
                <p className="text-sm text-neutral-500">
                  當對話達到此百分比時，提示用戶對話即將達到上限。
                </p>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <Slider
                      value={[settings.token_warning_threshold * 100]}
                      onValueChange={([value]) => setSettings({
                        ...settings,
                        token_warning_threshold: value / 100
                      })}
                      min={50}
                      max={95}
                      step={5}
                      className="w-full"
                    />
                  </div>
                  <span className="w-16 text-center font-medium">
                    {Math.round(settings.token_warning_threshold * 100)}%
                  </span>
                </div>
                <p className="text-xs text-neutral-400">
                  當前設定：達到 {(settings.max_context_tokens * settings.token_warning_threshold).toLocaleString()} tokens 時顯示警告
                </p>
              </div>
            </div>
          </section>

          {/* Case Analysis Agent Settings */}
          <section className="bg-white rounded-2xl p-6 shadow-sm border border-neutral-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">案例分析 Agent 設定</h2>
                <p className="text-sm text-neutral-500">配置自動分析影片的 AI Agent</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-base font-medium">AI 模型</Label>
                <p className="text-sm text-neutral-500 mb-2">
                  選擇用於分析影片的 AI 模型
                </p>
                <Select value={analysisModel} onValueChange={setAnalysisModel}>
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="選擇模型..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white max-h-[300px]">
                    {openrouterModels.map((model) => (
                      <SelectItem key={model.id} value={model.id} className="bg-white hover:bg-amber-50">
                        <div className="flex flex-col">
                          <span className="font-medium">{model.name}</span>
                          <span className="text-xs text-neutral-500">
                            ${(model.pricing.prompt * 1000000).toFixed(2)}/1M tokens
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-base font-medium">System Prompt</Label>
                <p className="text-sm text-neutral-500 mb-2">
                  定義 AI 分析影片文案的方式和輸出格式
                </p>
                <Textarea
                  value={analysisPrompt}
                  onChange={(e) => setAnalysisPrompt(e.target.value)}
                  placeholder="你是一個專業的短影片內容分析師..."
                  className="min-h-[300px] font-mono text-sm"
                />
                <p className="text-xs text-neutral-400 mt-2">
                  提示：使用 Markdown 格式定義輸出結構（## 標題、列表等）
                </p>
              </div>

              <Button
                onClick={saveAnalysisSettings}
                disabled={saving}
                className="rounded-full"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? '保存中...' : '保存分析設定'}
              </Button>
            </div>
          </section>
        </div>
      </main>
    </>
  );
};

export default AdminSettings;
